/**
 * AdminEditModal — edit display_name and replace role grants.
 *
 * Permissions:
 *   - display_name field shows if the current admin has admin.update
 *   - role checkboxes show if the current admin has admin.role.assign
 * If both are missing the modal still opens via the page-level "Edit"
 * button but everything is disabled — the modal explains why.
 *
 * Server-side guards enforced; we mirror the "can't revoke own
 * super_admin" rule visually by disabling the super_admin checkbox
 * when editing yourself.
 */
import { useEffect, useMemo, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { X } from 'lucide-react';
import { adminFetch } from '@/providers/admin-api';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import { toast } from '@/hooks/useToast';

interface Role {
  id: string;
  key: string;
  name: string;
  description: string;
  is_system: boolean;
}

interface AdminRow {
  id: string;
  email: string;
  display_name: string;
  role_keys: string[];
}

interface Props {
  admin: AdminRow;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
}

export function AdminEditModal({ admin, onClose, onSuccess }: Props) {
  const apiUrl = useApiUrl();
  const { admin: me, hasPermission } = useAdminPermissions();
  const canEditName = hasPermission('admin.update');
  const canEditRoles = hasPermission('admin.role.assign');

  const [displayName, setDisplayName] = useState(admin.display_name);
  const [roleKeys, setRoleKeys] = useState<string[]>(admin.role_keys);
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSelf = admin.id === me?.id;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await adminFetch(`${apiUrl}/v1/admin/roles`);
        const data: Role[] = await res.json();
        if (!cancelled) setRoles(data);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load roles');
      } finally {
        if (!cancelled) setRolesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  const toggleRole = (key: string) => {
    setRoleKeys(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key],
    );
  };

  // Build the request body — only include fields the user is allowed
  // to change AND that actually changed.
  const requestBody = useMemo(() => {
    const body: { display_name?: string; role_keys?: string[] } = {};
    if (canEditName && displayName.trim() !== admin.display_name) {
      body.display_name = displayName.trim();
    }
    if (canEditRoles) {
      const sortedNew = [...roleKeys].sort();
      const sortedOld = [...admin.role_keys].sort();
      if (
        sortedNew.length !== sortedOld.length ||
        sortedNew.some((k, i) => k !== sortedOld[i])
      ) {
        body.role_keys = roleKeys;
      }
    }
    return body;
  }, [canEditName, canEditRoles, displayName, roleKeys, admin]);

  const hasChanges = Object.keys(requestBody).length > 0;
  const canSubmit = hasChanges && !submitting && displayName.trim().length > 0;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await adminFetch(`${apiUrl}/v1/admin/admins/${admin.id}`, {
        method: 'PUT',
        body: JSON.stringify(requestBody),
      });
      toast({
        title: 'Admin updated',
        description: `Changes to ${admin.email} saved.`,
      });
      await onSuccess();
    } catch (e: any) {
      setError(e?.message || 'Failed to update admin');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg bg-card p-6">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold">Edit admin</h3>
            <p className="text-sm text-muted-foreground">{admin.email}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md p-1 hover:bg-muted disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {!canEditName && !canEditRoles && (
          <div className="mb-4 rounded-md border border-yellow-400 bg-yellow-50 p-3 text-sm text-yellow-900">
            You don't have permission to edit this admin. Contact a Super
            Admin if you need the <code>admin.update</code> or{' '}
            <code>admin.role.assign</code> permission.
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="edit-name" className="mb-1 block text-sm font-medium">
              Display name
            </label>
            <input
              id="edit-name"
              type="text"
              required
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              disabled={!canEditName || submitting}
              className="block w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-50"
            />
            {!canEditName && (
              <p className="mt-1 text-xs text-muted-foreground">
                Requires <code>admin.update</code> permission.
              </p>
            )}
          </div>

          <fieldset className="rounded-md border p-3">
            <legend className="px-1 text-sm font-medium">Roles</legend>
            {!canEditRoles && (
              <p className="mb-2 text-xs text-muted-foreground">
                Requires <code>admin.role.assign</code> permission to change.
              </p>
            )}
            {rolesLoading ? (
              <p className="text-sm text-muted-foreground">Loading roles…</p>
            ) : (
              <div className="space-y-2">
                {roles.map(r => {
                  const isChecked = roleKeys.includes(r.key);
                  // Self-action guard: you cannot revoke your own
                  // super_admin role. The backend enforces this; we
                  // disable the checkbox so it's a visible constraint.
                  const ownSuperRevoke =
                    isSelf && r.key === 'super_admin' && isChecked;
                  return (
                    <label
                      key={r.id}
                      className={
                        canEditRoles && !ownSuperRevoke
                          ? 'flex cursor-pointer items-start gap-2 rounded-md p-2 hover:bg-muted'
                          : 'flex items-start gap-2 rounded-md p-2 opacity-60'
                      }
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleRole(r.key)}
                        disabled={!canEditRoles || submitting || ownSuperRevoke}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium">
                          {r.name}{' '}
                          <code className="text-xs text-muted-foreground">
                            ({r.key})
                          </code>
                        </div>
                        {r.description && (
                          <div className="text-xs text-muted-foreground">
                            {r.description}
                          </div>
                        )}
                        {ownSuperRevoke && (
                          <div className="text-xs italic text-yellow-700">
                            You cannot revoke your own Super Admin role.
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </fieldset>

          {error && (
            <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-md border bg-card px-4 py-2 text-sm hover:bg-muted disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {submitting ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
